import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const HEADERS = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
});

function Products() {
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [lowStockFilter, setLowStockFilter] = useState(false);
  
  // Transfer state
  const [selectedProductForTransfer, setSelectedProductForTransfer] = useState(null);
  const [transferQty, setTransferQty] = useState(1);
  const [targetBranchId, setTargetBranchId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');

  const [newProduct, setNewProduct] = useState({
    name: '',
    brand: '',
    sku: '',
    barcode: '',
    category: '',
    description: '',
    sale_price: 0,
    cost_price: 0,
    stock_quantity: 0,
    min_stock_level: 5,
    unit: 'adet',
    is_for_sale: true,
    is_for_internal: true
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [lowStockFilter, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches', HEADERS());
      setBranches(response.data);
      if (response.data.length > 0 && !selectedBranch) {
        // Set default to first branch
        setSelectedBranch(response.data[0].id);
      }
    } catch (error) {
      console.error('Fetch branches error:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url = `/api/products?low_stock=${lowStockFilter}`;
      if (selectedBranch) {
        url += `&branch_id=${selectedBranch}`;
      }
      const response = await axios.get(url, HEADERS());
      setProducts(response.data);
    } catch (error) {
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/products', newProduct, HEADERS());
      setShowModal(false);
      setNewProduct({
        name: '', brand: '', sku: '', barcode: '', category: '',
        description: '', sale_price: 0, cost_price: 0, stock_quantity: 0,
        min_stock_level: 5, unit: 'adet', is_for_sale: true, is_for_internal: true
      });
      fetchProducts();
    } catch (error) {
      alert('Ürün eklenemedi');
    }
  };

  const handleStockUpdate = async (productId, type, quantity) => {
    try {
      await axios.post(`/api/products/${productId}/transactions`, {
        type,
        quantity: parseInt(quantity),
        notes: 'Manuel stok güncellemesi'
      }, HEADERS());
      fetchProducts();
    } catch (error) {
      alert('Stok güncellenemedi');
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForTransfer || !targetBranchId) return;

    try {
      await axios.post('/api/stock-transfers', {
        from_branch_id: selectedBranch,
        to_branch_id: targetBranchId,
        product_id: selectedProductForTransfer.id,
        quantity: transferQty,
        notes: transferNotes
      }, HEADERS());
      
      setShowTransferModal(false);
      setTransferNotes('');
      setTransferQty(1);
      fetchProducts();
      alert('Stok transferi başarıyla tamamlandı!');
    } catch (error) {
      alert(error.response?.data?.error || 'Stok transferi başarısız oldu');
    }
  };

  const openTransferModal = (product) => {
    setSelectedProductForTransfer(product);
    setTargetBranchId(branches.find(b => b.id !== selectedBranch)?.id || '');
    setTransferQty(1);
    setShowTransferModal(true);
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  // Financial Metrics
  const totalCostValue = products.reduce((acc, curr) => acc + (parseFloat(curr.cost_price || 0) * curr.stock_quantity), 0);
  const totalSaleValue = products.reduce((acc, curr) => acc + (parseFloat(curr.sale_price || 0) * curr.stock_quantity), 0);
  const totalPotentialProfit = totalSaleValue - totalCostValue;
  const criticalStockCount = products.filter(p => p.stock_quantity <= p.min_stock_level).length;

  // Chart Data (top 7 products by stock value)
  const sortedByVal = [...products].sort((a,b) => (b.sale_price * b.stock_quantity) - (a.sale_price * a.stock_quantity)).slice(0, 7);
  
  const chartData = {
    labels: sortedByVal.map(p => p.name.substring(0, 15)),
    datasets: [
      {
        label: 'Stok Adeti',
        data: sortedByVal.map(p => parseInt(p.stock_quantity || 0)),
        backgroundColor: 'rgba(59, 130, 246, 0.65)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: 'y1',
      },
      {
        label: 'Maliyet (TL)',
        data: sortedByVal.map(p => parseFloat(p.cost_price || 0)),
        backgroundColor: 'rgba(239, 68, 68, 0.65)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        label: 'Satış Fiyatı (TL)',
        data: sortedByVal.map(p => parseFloat(p.sale_price || 0)),
        backgroundColor: 'rgba(16, 185, 129, 0.65)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: 'y',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#f3f4f6',
          font: { family: 'Plus Jakarta Sans', weight: 500 }
        }
      },
      title: {
        display: true,
        text: 'En Değerli Ürünlerin Stok ve Fiyat Analizi',
        color: '#f3f4f6',
        font: { family: 'Plus Jakarta Sans', size: 14, weight: 600 }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans', size: 10 } }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans' } }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: { color: 'rgb(59, 130, 246)', font: { family: 'Plus Jakarta Sans' } }
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      
      {/* HEADER SECTION */}
      <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Ürünler & Envanter Yönetimi</h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Aktif Şube:</span>
            <select 
              className="input" 
              style={{ width: 'auto', padding: '0.25rem 2rem 0.25rem 0.75rem', margin: 0, height: '36px' }}
              value={selectedBranch}
              onChange={e => setSelectedBranch(parseInt(e.target.value))}
            >
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className={`btn ${lowStockFilter ? 'btn-danger' : 'btn-secondary'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', height: '42px' }}
            onClick={() => setLowStockFilter(!lowStockFilter)}
          >
            ⚠️ {lowStockFilter ? 'Filtreyi Kaldır' : 'Kritik Stokları Filtrele'}
          </button>
          <button className="btn btn-primary" style={{ height: '42px' }} onClick={() => setShowModal(true)}>
            + Yeni Ürün Ekle
          </button>
        </div>
      </div>

      {/* METRICS CARDS */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '1rem' 
      }}>
        <div className="card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Toplam Envanter Değeri (Maliyet)</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-color)' }}>
            {totalCostValue.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
          </span>
        </div>
        <div className="card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Toplam Satış Değeri</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
            {totalSaleValue.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
          </span>
        </div>
        <div className="card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Potansiyel Net Brüt Kâr</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>
            {totalPotentialProfit.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
          </span>
        </div>
        <div className="card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Kritik Stoktaki Ürünler</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: criticalStockCount > 0 ? '#ef4444' : 'var(--text-color)' }}>
            {criticalStockCount} adet
          </span>
        </div>
      </div>

      {/* GRAPH & ANALYSIS SECTION */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', alignItems: 'stretch' }}>
        <div className="card" style={{ padding: '1.5rem', minHeight: '320px' }}>
          {products.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              Grafik için ürün bulunmuyor
            </div>
          ) : (
            <div style={{ position: 'relative', height: '280px', width: '100%' }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}
        </div>
        
        {/* PROFIT ANALYSIS PANEL */}
        <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <h4 style={{ margin: '0 0 1rem 0', fontWeight: 600, fontSize: '1.1rem' }}>Maliyet & Kâr Özeti</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9ca3af' }}>Brüt Kâr Oranı:</span>
                <span style={{ fontWeight: 600, color: '#10b981' }}>
                  {totalSaleValue > 0 ? ((totalPotentialProfit / totalSaleValue) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <hr style={{ border: '0', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '0.5rem 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9ca3af' }}>Kritik Seviye:</span>
                <span>{criticalStockCount} Ürün Düşük</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9ca3af' }}>Kategori Sayısı:</span>
                <span>{categories.length} Farklı Kategori</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#9ca3af', borderLeft: '3px solid #3b82f6', paddingLeft: '0.75rem', marginTop: '1rem' }}>
            Buradaki veriler aktif seçili şubeye aittir. Karlılık hesaplamaları anlık stok adetleri üzerinden hesaplanır.
          </div>
        </div>
      </div>

      {/* PRODUCTS TABLE */}
      <div className="card" style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>Yükleniyor...</div>
        ) : products.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>Ürün bulunamadı.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Ürün Bilgisi</th>
                <th>Kategori</th>
                <th style={{ textAlign: 'right' }}>Stok</th>
                <th style={{ textAlign: 'right' }}>Maliyet</th>
                <th style={{ textAlign: 'right' }}>Satış Fiyatı</th>
                <th style={{ textAlign: 'center' }}>Durum</th>
                <th style={{ textAlign: 'center' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{product.name}</div>
                    {product.brand && <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>{product.brand}</div>}
                    {product.sku && <div className="text-muted" style={{ fontSize: '0.75rem' }}>SKU: {product.sku}</div>}
                  </td>
                  <td>
                    <span className="badge badge-secondary" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      {product.category || 'Belirtilmemiş'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ 
                      fontWeight: 700,
                      color: product.stock_quantity <= product.min_stock_level ? '#ef4444' : 'inherit'
                    }}>
                      {product.stock_quantity} {product.unit}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {parseFloat(product.cost_price || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                    {parseFloat(product.sale_price || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {product.stock_quantity <= product.min_stock_level ? (
                      <span className="badge badge-danger">Kritik</span>
                    ) : (
                      <span className="badge badge-success">Güvenli</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center' }}>
                      <button 
                        className="btn btn-success" 
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}
                        title="Stok Arttır"
                        onClick={() => handleStockUpdate(product.id, 'purchase', 1)}
                      >
                        +
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}
                        title="Stok Azalt"
                        onClick={() => handleStockUpdate(product.id, 'usage', 1)}
                      >
                        -
                      </button>
                      {branches.length > 1 && (
                        <button 
                          className="btn btn-primary"
                          style={{ padding: '0.3rem 0.65rem', fontSize: '0.8rem', background: '#3b82f6' }}
                          onClick={() => openTransferModal(product)}
                        >
                          Transfer Et
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* NEW PRODUCT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Yeni Ürün Tanımla</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'inherit' }}>×</button>
            </div>

            <form onSubmit={handleCreateProduct}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Ürün Adı *</label>
                  <input
                    type="text"
                    className="input"
                    value={newProduct.name}
                    onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Marka</label>
                  <input
                    type="text"
                    className="input"
                    value={newProduct.brand}
                    onChange={e => setNewProduct({ ...newProduct, brand: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input
                    type="text"
                    className="input"
                    value={newProduct.sku}
                    onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Barkod</label>
                  <input
                    type="text"
                    className="input"
                    value={newProduct.barcode}
                    onChange={e => setNewProduct({ ...newProduct, barcode: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Kategori</label>
                <input
                  type="text"
                  className="input"
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                  list="categories"
                />
                <datalist id="categories">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Maliyet *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={newProduct.cost_price}
                    onChange={e => setNewProduct({ ...newProduct, cost_price: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Satış Fiyatı *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={newProduct.sale_price}
                    onChange={e => setNewProduct({ ...newProduct, sale_price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Başlangıç Stok Adeti</label>
                  <input
                    type="number"
                    className="input"
                    value={newProduct.stock_quantity}
                    onChange={e => setNewProduct({ ...newProduct, stock_quantity: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Minimum Stok Seviyesi</label>
                  <input
                    type="number"
                    className="input"
                    value={newProduct.min_stock_level}
                    onChange={e => setNewProduct({ ...newProduct, min_stock_level: e.target.value })}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem', height: '45px' }}>
                Ürünü Kaydet
              </button>
            </form>
          </div>
        </div>
      )}

      {/* STOCK TRANSFER MODAL */}
      {showTransferModal && selectedProductForTransfer && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Şubeler Arası Stok Transferi</h3>
              <button onClick={() => setShowTransferModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'inherit' }}>×</button>
            </div>

            <form onSubmit={handleTransferSubmit}>
              <div style={{ marginBottom: '1.25rem', padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                <div style={{ fontWeight: 600 }}>{selectedProductForTransfer.name}</div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '2px' }}>
                  Mevcut Stok: {selectedProductForTransfer.stock_quantity} {selectedProductForTransfer.unit}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Hedef Şube *</label>
                <select 
                  className="input" 
                  value={targetBranchId}
                  onChange={e => setTargetBranchId(parseInt(e.target.value))}
                  required
                >
                  <option value="">Hedef Şube Seçin</option>
                  {branches.filter(b => b.id !== selectedBranch).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Transfer Edilecek Miktar * (Maks: {selectedProductForTransfer.stock_quantity})</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  max={selectedProductForTransfer.stock_quantity}
                  value={transferQty}
                  onChange={e => setTransferQty(parseInt(e.target.value))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Transfer Notu / Açıklama</label>
                <textarea
                  className="input"
                  rows="3"
                  value={transferNotes}
                  onChange={e => setTransferNotes(e.target.value)}
                  placeholder="Transfer sebebi veya kargo takip no..."
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: '1rem', height: '45px', background: '#3b82f6' }}
                disabled={transferQty > selectedProductForTransfer.stock_quantity || transferQty <= 0}
              >
                Transferi Başlat ve Gönder
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default Products;